import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getCampaigns, getCampaignRequestLogs } from '../../services/campaigns';
import { useCampaignStatistics } from '../../hooks/useStatistics';
import { LineChart } from '../../components/charts/LineChart';
import { BarChart } from '../../components/charts/BarChart';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Accordion } from '../../components/ui/Accordion';
import { SkeletonStatistics, Spinner } from '../../components/ui';
import { cn } from '../../lib/utils';
import type { StatisticsPeriod } from '../../types/statistics';

type DateRangeOption = {
  labelKey: string;
  value: StatisticsPeriod;
};

const DATE_RANGES: DateRangeOption[] = [
  { labelKey: 'statistics.today', value: 'today' },
  { labelKey: 'statistics.last_7_days', value: 'last_7_days' },
  { labelKey: 'statistics.last_30_days', value: 'last_30_days' },
  { labelKey: 'statistics.last_90_days', value: 'last_90_days' },
];

export default function StatisticsPage() {
  const { t } = useTranslation('campaigns');
  const [searchParams, setSearchParams] = useSearchParams();

  // Get campaignId from URL params or state
  const urlCampaignId = searchParams.get('campaignId');
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(
    urlCampaignId ? parseInt(urlCampaignId, 10) : null
  );
  const [selectedPeriod, setSelectedPeriod] = useState<StatisticsPeriod>('today');
  const [requestLogsPage, setRequestLogsPage] = useState<number>(1);

  // Fetch campaigns for dropdown
  const { data: campaignsData, isLoading: loadingCampaigns } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => getCampaigns(),
  });

  const campaigns = campaignsData?.campaigns || [];

  // Auto-select first campaign if none selected and URL param not set
  useEffect(() => {
    if (campaigns.length > 0 && !selectedCampaignId) {
      const firstCampaignId = campaigns[0].id;
      setSelectedCampaignId(firstCampaignId);
      // Update URL to reflect auto-selection
      setSearchParams({ campaignId: firstCampaignId.toString() });
    }
  }, [campaigns, selectedCampaignId, setSearchParams]);

  // Update selected campaign when URL changes (browser back/forward)
  useEffect(() => {
    if (urlCampaignId) {
      const id = parseInt(urlCampaignId, 10);
      if (id !== selectedCampaignId) {
        setSelectedCampaignId(id);
      }
    }
  }, [urlCampaignId]);

  // Reset pagination when period changes
  useEffect(() => {
    setRequestLogsPage(1);
  }, [selectedPeriod]);

  // Fetch statistics for selected campaign
  const { data: statistics, isLoading, isError, error, refetch, isFetching } = useCampaignStatistics(
    selectedCampaignId || 0,
    { period: selectedPeriod },
    { enabled: !!selectedCampaignId }
  );

  // Fetch paginated request logs separately
  const { data: requestLogsData, isLoading: isLoadingLogs, refetch: refetchLogs } = useQuery({
    queryKey: ['campaign-request-logs', selectedCampaignId, selectedPeriod, requestLogsPage],
    queryFn: () =>
      getCampaignRequestLogs(selectedCampaignId!, {
        page: requestLogsPage,
        per_page: 50,
        period: selectedPeriod,
      }),
    enabled: !!selectedCampaignId,
  });

  // Handle campaign change from dropdown
  const handleCampaignChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const campaignId = parseInt(e.target.value, 10);
    setSelectedCampaignId(campaignId);
    // Update URL with new campaign ID
    setSearchParams({ campaignId: campaignId.toString() });
  };

  // Loading state
  if (loadingCampaigns) {
    return (
      <div className="p-6">
        <Card>
          <div className="p-12 flex flex-col items-center justify-center">
            <Spinner size="lg" />
            <p className="text-gray-600 mt-4">{t('statistics.loading', 'Loading...')}</p>
          </div>
        </Card>
      </div>
    );
  }

  // No campaigns state
  if (campaigns.length === 0) {
    return (
      <div className="p-6">
        <Alert variant="warning">
          {t('statistics.no_campaigns', 'No campaigns available. Create a campaign first.')}
        </Alert>
      </div>
    );
  }

  // Loading statistics
  if (isLoading) {
    return <SkeletonStatistics />;
  }

  // Error state
  if (isError) {
    return (
      <div className="p-6">
        <Alert variant="error">
          {t('statistics.error')}: {error instanceof Error ? error.message : t('statistics.unknown')}
        </Alert>
      </div>
    );
  }

  // No statistics data
  if (!statistics) {
    return (
      <div className="p-6">
        <Alert variant="warning">{t('statistics.error')}</Alert>
      </div>
    );
  }

  const { summary, daily_stats, top_countries, top_user_agents, top_referrers, campaign_name } = statistics;

  // Format chart data based on period (30-min for today, 3-hour for 7 days, daily for 30 days, 3-day for 90 days)
  const chartData = daily_stats.map((stat: any, index: number) => {
    let formattedLabel;

    // Check if data includes 'hour' field (30-min or 3-hour breakdown)
    if ('hour' in stat) {
      const dateObj = new Date(stat.hour);
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');

      // For multi-day periods (7 days), show date + time
      // For single day (today), show just time
      if (selectedPeriod === 'last_7_days') {
        // 3-hour interval format: "Jan 24 00:00", "Jan 24 03:00"
        const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
        const day = dateObj.getDate();
        formattedLabel = `${month} ${day} ${hours}:${minutes}`;
      } else {
        // 30-minute interval format: "14:00", "14:30"
        formattedLabel = `${hours}:${minutes}`;
      }
    } else {
      // Daily or 3-day format
      const dateObj = new Date(stat.date);
      const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
      const day = dateObj.getDate();

      if (selectedPeriod === 'last_90_days') {
        // 3-day period format: "Jan 24" (start of 3-day period)
        formattedLabel = `${month} ${day}`;
      } else {
        // Daily format: "Jan 22"
        formattedLabel = `${month} ${day}`;
      }
    }

    return {
      ...stat,
      date: formattedLabel,
    };
  });

  return (
    <div className="p-4 space-y-4">
      {/* Header with Campaign Selector */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('statistics.title', { name: campaign_name })}</h1>
          <p className="text-gray-600 text-sm mt-1">{t('statistics.subtitle')}</p>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* Campaign Selector */}
          <div className="w-full sm:w-auto">
            <select
              value={selectedCampaignId || ''}
              onChange={handleCampaignChange}
              className="block w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm font-medium"
            >
              {campaigns.map((campaign: any) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </div>

          {/* Refresh Button */}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <svg
              className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {isFetching ? t('statistics.refreshing') : t('statistics.refresh')}
          </button>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="flex flex-wrap gap-2">
        {DATE_RANGES.map((range) => (
          <button
            key={range.value}
            onClick={() => setSelectedPeriod(range.value)}
            className={cn(
              'px-4 py-2 rounded-lg font-medium transition-colors text-sm',
              selectedPeriod === range.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            {t(range.labelKey)}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="p-4">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('statistics.total_requests')}</h3>
            <p
              data-testid="total-requests"
              className="text-2xl font-bold text-gray-900 mt-1"
            >
              {summary.total_requests.toLocaleString()}
            </p>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('statistics.human_requests')}</h3>
            <p
              data-testid="human-count"
              className="text-2xl font-bold text-green-600 mt-1"
            >
              {summary.human_count.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t('statistics.percentage_of_total', { percentage: summary.human_percentage.toFixed(1) })}
            </p>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('statistics.bot_requests')}</h3>
            <p
              data-testid="bot-count"
              className="text-2xl font-bold text-red-600 mt-1"
            >
              {summary.bot_count.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t('statistics.percentage_of_total', { percentage: summary.bot_percentage.toFixed(1) })}
            </p>
          </div>
        </Card>
      </div>

      {/* Requests Over Time - Full Width */}
      <Card>
        <div className="p-4">
          <div data-testid="daily-chart">
            <LineChart
              data={chartData}
              title={t('statistics.requests_over_time')}
              xAxisKey="date"
              lines={[
                { dataKey: 'human', stroke: '#10b981', name: t('statistics.human_label') },
                { dataKey: 'bot', stroke: '#ef4444', name: t('statistics.bot_label') },
                { dataKey: 'total', stroke: '#3b82f6', name: t('statistics.total_label') },
              ]}
              height={300}
            />
          </div>
        </div>
      </Card>

      {/* Top Countries Chart */}
      <Accordion title={t('statistics.top_countries')} defaultOpen={false}>
        <div className="p-4">
          <div data-testid="countries-chart">
            <BarChart
              data={top_countries}
              title=""
              xAxisKey="country"
              dataKey="count"
              barColor="#3b82f6"
              height={280}
            />
          </div>
        </div>
      </Accordion>

      {/* Tables Row: User Agents + Referrers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top User Agents */}
        <Accordion title={t('statistics.top_user_agents')} defaultOpen={false}>
          <div className="p-4">
            <div data-testid="user-agents-table" className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('statistics.user_agent_header')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('statistics.requests_header')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {top_user_agents.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-4 py-3 text-center text-gray-500 text-sm">
                        {t('statistics.no_traffic_data')}
                      </td>
                    </tr>
                  ) : (
                    top_user_agents.map((ua, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm text-gray-900 max-w-md truncate" title={ua.user_agent}>
                          {ua.user_agent}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {ua.count.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Accordion>

        {/* Top Referrers */}
        <Accordion title={t('statistics.top_referrers')} defaultOpen={false}>
          <div className="p-4">
            <div data-testid="referrers-table" className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('statistics.referrer_header')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('statistics.requests_header')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {top_referrers.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-4 py-3 text-center text-gray-500 text-sm">
                        {t('statistics.no_traffic_data')}
                      </td>
                    </tr>
                  ) : (
                    top_referrers.map((ref, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm text-gray-900 max-w-md truncate" title={ref.referer}>
                          {ref.referer || t('statistics.direct')}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {ref.count.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Accordion>
      </div>

      {/* Request Logs Table */}
      <Card>
        <div className="p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('statistics.request_logs', 'Recent Request Logs')}
          </h2>

          {isLoadingLogs ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('statistics.timestamp', 'Timestamp')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('statistics.decision', 'Decision')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('statistics.ip_address', 'IP Address')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('statistics.country', 'Country')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('statistics.language', 'Language')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('statistics.browser', 'Browser')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('statistics.version', 'Version')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('statistics.platform', 'Platform')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('statistics.domain', 'Domain')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {!requestLogsData || requestLogsData.data.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-3 text-center text-gray-500 text-sm">
                          {t('statistics.no_request_logs', 'No request logs available for this period.')}
                        </td>
                      </tr>
                    ) : (
                      requestLogsData.data.map((log: any, index: number) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm">
                            <span
                              className={cn(
                                'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                                log.decision === 'Human'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              )}
                            >
                              {log.decision}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 font-mono">
                            {log.ip_address}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {log.country}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {log.language}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {log.browser}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {log.version}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {log.platform}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 max-w-xs truncate" title={log.domain}>
                            {log.domain}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {requestLogsData && requestLogsData.total > 0 && (
                <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                  <div className="text-sm text-gray-700">
                    Showing {requestLogsData.from} to {requestLogsData.to} of {requestLogsData.total} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setRequestLogsPage((p) => Math.max(1, p - 1))}
                      disabled={requestLogsPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-700">
                      Page {requestLogsData.current_page} of {requestLogsData.last_page}
                    </span>
                    <button
                      onClick={() => setRequestLogsPage((p) => Math.min(requestLogsData.last_page, p + 1))}
                      disabled={requestLogsPage === requestLogsData.last_page}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
